import { useAction, useAtom } from '@reatom/npm-react'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PlusCircleIcon } from '@blade/ui-kit'
import { walletsAtom, type WalletModel } from '@root/entities/wallet'
import { WalletsDialog } from '@root/widgets/desktop/wallets-dialog'

import type { DistributeFundsModel } from '../../atoms'
import type { DialogControlProps } from '@root/shared/ui-kit'

type Props = {
  model: DistributeFundsModel
} & DialogControlProps

export const WalletDestinationSelectionDialog: React.FC<Props> = ({
  model,
  ...dialogProps
}) => {
  const { t } = useTranslation()

  const [targets] = useAtom(model.targets)
  const setTargets = useAction(model.setTargets)
  const [wallets] = useAtom(walletsAtom)
  const [sourceWallet] = useAtom(model.sourceWallet)

  const selectionWallets = useMemo(() => {
    if (!sourceWallet) {
      return wallets
    }
    return wallets.filter((it) => it.id !== sourceWallet.id)
  }, [wallets, sourceWallet])

  const [locallySelectedWallets, setLocallySelectedWallets] = useState<
    Array<WalletModel>
  >(targets.map((it) => it.wallet))

  const locallySelectedWalletIds = useMemo(
    () => locallySelectedWallets.map((wallet) => wallet.id),
    [locallySelectedWallets],
  )

  useEffect(() => {
    setLocallySelectedWallets(targets.map((it) => it.wallet))
  }, [targets])

  return (
    <WalletsDialog
      {...dialogProps}
      multiple
      wallets={selectionWallets}
      selectedWalletIds={locallySelectedWalletIds}
      onWalletsSelected={(wallets) => {
        setLocallySelectedWallets(wallets)
      }}
      footerContent={
        <WalletsDialog.Button
          iconLeft={<PlusCircleIcon />}
          onClick={() => {
            setTargets(locallySelectedWallets)
            dialogProps.onOpenChange?.(false)
          }}
        >
          {t('wallet.distribute.add-wallets')}
        </WalletsDialog.Button>
      }
    />
  )
}
